import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe2, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import Flag from '../world/Flag';
import { API } from '../../lib/api';
import { POLOZKY } from '../world/CountryProfile';
import { prelozit } from '../../lib/i18n';

/**
 * Vyplňování profilu země – co v ní pro trans lidi platí.
 *
 * Formulář je schválně otravný v jedné věci: u každého údaje chce zdroj.
 * Právní a zdravotnické informace bez toho, odkud jsou, se nedají použít
 * a tady se podle nich lidé rozhodují, kam pojedou nebo kam se odstěhují.
 *
 * Datum poslední kontroly dopisuje server sám při uložení. Kdyby ho vyplňoval
 * člověk, brzy by tam stálo něco jiného, než kdy se to opravdu ověřovalo — a
 * právě podle data se pozná, jestli se údaji dá věřit.
 */

const STAVY = [
  ['unknown', 'Nevíme'],
  ['yes', 'Ano'],
  ['limited', 'Částečně'],
  ['no', 'Ne'],
];

const prazdnyUdaj = () => ({ status: 'unknown', note_cs: '', note_en: '', source: '' });

export default function AdminCountryProfile() {
  const [zeme, setZeme] = useState([]);
  const [hledani, setHledani] = useState('');
  const [vybrana, setVybrana] = useState(null);
  const [intro, setIntro] = useState({ cs: '', en: '' });
  const [fakta, setFakta] = useState({});
  const [uklada, setUklada] = useState(false);

  useEffect(() => {
    axios.get(`${API}/geo/countries`).then((r) => setZeme(r.data)).catch(() => {});
  }, []);

  const otevri = async (z) => {
    try {
      // Veřejné rozhraní neověřené údaje nevydává; administrace je posuzuje,
      // takže si je bere zvláštní cestou.
      const { data } = await axios.get(`${API}/admin/geo/countries/${z.iso2}/profile`);
      setVybrana({ ...data, slug: z.slug });
      setIntro({ cs: data.intro_cs || '', en: data.intro_en || '' });
      setFakta(Object.fromEntries(Object.keys(POLOZKY).map((k) =>
        [k, { ...prazdnyUdaj(), ...(data.facts?.[k] || {}) }])));
    } catch {
      toast.error('Zemi se nepodařilo načíst.');
    }
  };

  const uloz = async () => {
    // Posílají se všechny položky včetně vyprázdněných — právě podle nich server
    // pozná, co má smazat. Bez toho by se údaj vyplněný omylem nedal vzít zpět.
    const kUlozeni = fakta;

    setUklada(true);
    try {
      await axios.put(`${API}/admin/geo/countries/${vybrana.iso2}/profile`, {
        intro_cs: intro.cs,
        intro_en: intro.en,
        facts: kUlozeni,
      });
      toast.success('Uloženo.');
      otevri(vybrana);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Uložit se to nepovedlo.');
    } finally {
      setUklada(false);
    }
  };

  const zmen = (klic, pole, hodnota) =>
    setFakta((p) => ({ ...p, [klic]: { ...p[klic], [pole]: hodnota } }));

  const nalezene = zeme.filter((z) =>
    !hledani || (z.name_cs || '').toLowerCase().includes(hledani.toLowerCase())
    || (z.name_en || '').toLowerCase().includes(hledani.toLowerCase()));

  if (vybrana) {
    return (
      <div className="space-y-5" data-testid="admin-profil-zeme">
        <div className="flex items-center gap-3 flex-wrap">
          <Flag emoji={vybrana.flag} iso2={vybrana.iso2} size="md" />
          <h3 className="font-heading font-bold text-bloom-text text-lg flex-1">
            {vybrana.name_cs}
          </h3>
          <Button variant="outline" size="sm" onClick={() => setVybrana(null)}>
            Zpět na seznam
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-sm">Úvod (česky)</Label>
            <Textarea value={intro.cs} onChange={(e) => setIntro({ ...intro, cs: e.target.value })}
                      rows={5} className="mt-1" placeholder="Pár vět o tom, jak to v téhle zemi vypadá."
                      data-testid="intro-cs" />
          </div>
          <div>
            <Label className="text-sm">Úvod (anglicky)</Label>
            <Textarea value={intro.en} onChange={(e) => setIntro({ ...intro, en: e.target.value })}
                      rows={5} className="mt-1" />
          </div>
        </div>

        <div className="space-y-3">
          {Object.entries(POLOZKY).map(([klic, polozka]) => (
            <div key={klic} className="rounded-2xl border border-border/40 bg-white p-3.5">
              {/* Administrace je česky, tak se bere rovnou český název. */}
              <div className="flex items-start gap-2 flex-wrap">
                <p className="font-medium text-bloom-text text-sm flex-1 min-w-[10rem]">
                  {prelozit('cs', `profil.${klic}`)}
                </p>
                {/* Ať je na první pohled vidět, co se členům neukáže a proč. */}
                {(() => {
                  const ov = fakta[klic]?.verification_status;
                  if (ov === 'source_checked') return null;
                  const popis = {
                    first_pass: 'z prvního průchodu — skryté',
                    source_checked_needs_primary_detail: 'chybí doklad v zákoně — skryté',
                    unknown: 'neověřené — skryté',
                  }[ov] || (fakta[klic]?.status && fakta[klic].status !== 'unknown'
                    ? 'neověřené — skryté' : null);
                  return popis ? (
                    <span className="text-[11px] bg-amber-50 border border-amber-200/70 text-amber-800 rounded-lg px-2 py-0.5">
                      {popis}
                    </span>
                  ) : null;
                })()}
              </div>
              <p className="text-xs text-bloom-sub">{polozka.otazka}</p>

              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {STAVY.map(([hodnota, popisek]) => (
                  <button key={hodnota} type="button"
                          onClick={() => zmen(klic, 'status', hodnota)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            fakta[klic]?.status === hodnota
                              ? 'bg-bloom-violet text-white border-bloom-violet'
                              : 'bg-white text-bloom-sub border-border/50 hover:border-bloom-violet/40'
                          }`}
                          data-testid={`stav-${klic}-${hodnota}`}>
                    {popisek}
                  </button>
                ))}
              </div>

              <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
                <Textarea value={fakta[klic]?.note_cs || ''} rows={2}
                          onChange={(e) => zmen(klic, 'note_cs', e.target.value)}
                          placeholder="Poznámka česky" />
                <Textarea value={fakta[klic]?.note_en || ''} rows={2}
                          onChange={(e) => zmen(klic, 'note_en', e.target.value)}
                          placeholder="Poznámka anglicky" />
              </div>
              <Input value={fakta[klic]?.source || ''} className="mt-2"
                     onChange={(e) => zmen(klic, 'source', e.target.value)}
                     placeholder="Odkud to je — odkaz na zákon, úřad nebo organizaci" />
              {fakta[klic]?.checked_at && (
                <p className="mt-1.5 text-[11px] text-bloom-sub/80">
                  Naposledy ověřeno {new Date(fakta[klic].checked_at)
                    .toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-bloom-sub bg-bloom-bg border border-border/50 rounded-lg px-3 py-2 leading-relaxed">
          <strong>Co uložíš tady, se tím považuje za ověřené a začne se členům
          zobrazovat.</strong> Údaje označené jako skryté pocházejí z rešerše,
          kterou zatím nikdo neprošel u zdroje — dokud je nepotvrdíš, nikdo je
          neuvidí.{' '}
          Co necháš na „nevíme" a bez poznámky, se na stránce země vůbec neukáže —
          a když to tak nastavíš u údaje, který už vyplněný byl, smaže se. Prázdný
          údaj se totiž nesmí tvářit jako „nejde to" a odrazovat někoho od země,
          kam mohl klidně jet.
        </p>

        <Button onClick={uloz} disabled={uklada}
                className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid="ulozit-profil-zeme">
          {uklada ? 'Ukládám…' : 'Uložit profil země'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-profil-zeme-seznam">
      <div>
        <h3 className="font-heading font-bold text-bloom-text flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-bloom-violet" />
          Profily zemí
        </h3>
        <p className="mt-1 text-sm text-bloom-sub">
          Co v které zemi pro trans lidi platí — změna jména, dostupnost péče,
          bezpečnost. Vyplňuje se ručně a u každého údaje je vidět zdroj i datum.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
        <Input value={hledani} onChange={(e) => setHledani(e.target.value)}
               placeholder="Hledat zemi…" className="pl-9" />
      </div>

      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {nalezene.slice(0, 60).map((z) => {
          const vyplneno = Object.values(z.facts || {})
            .filter((u) => u?.status && u.status !== 'unknown').length;
          return (
            <li key={z.iso2}>
              <button type="button" onClick={() => otevri(z)}
                      className="w-full text-left bg-white border border-border/40 rounded-xl p-3 hover:border-bloom-violet/40 flex items-center gap-2.5">
                <Flag emoji={z.flag} iso2={z.iso2} size="sm" />
                <span className="flex-1 min-w-0 truncate text-sm text-bloom-text">{z.name_cs}</span>
                <span className={`text-xs ${vyplneno ? 'text-bloom-mint' : 'text-bloom-sub/60'}`}>
                  {vyplneno ? `${vyplneno}/6` : 'prázdné'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {nalezene.length > 60 && (
        <p className="text-xs text-bloom-sub">Zobrazeno prvních 60 — hledej podle názvu.</p>
      )}
    </div>
  );
}
